// libs/sdk/src/lib/wallet/connect-wallet-controller.ts
import { debounceTime, defaultIfEmpty as defaultIfEmpty2, fromEvent, Subject, take, takeUntil as takeUntil2, tap as tap2, timer as timer3 } from "rxjs";

// libs/sdk/src/lib/wallet/adapters/create-client-and-sync-chain.ts
import { createWalletClient, custom } from "viem";
import { getChainById } from "@one-inch-community/sdk/chain";
async function createClientAndSyncChain(chainId, provider, address) {
  const client = createClient(
    chainId,
    provider,
    address
  );
  await client.requestAddresses().catch((error) => {
    if (error.core === 4001 || error.message?.toLowerCase()?.includes("user reject") || error.details === "Cancelled") throw error;
  });
  const walletChainId = await client.getChainId();
  if (chainId !== walletChainId) {
    await client.switchChain(getChainById(chainId));
  }
  return client;
}
function createClient(chainId, provider, address) {
  return createWalletClient({
    chain: getChainById(chainId),
    transport: custom(provider),
    account: address
  });
}

// libs/sdk/src/lib/wallet/provider-data-adapter.ts
import {
  BehaviorSubject,
  filter,
  from,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  combineLatest,
  defer,
  merge
} from "rxjs";

// libs/sdk/src/lib/wallet/from-eip1193-event.ts
import { Observable } from "rxjs";
function fromEIP1193Event(target, eventName) {
  return new Observable((subscriber) => {
    const handler = (event) => {
      subscriber.next(event);
    };
    target.on(eventName, handler);
    return () => {
      if ("removeListener" in target) {
        return target.removeListener(eventName, handler);
      }
      console.warn("unable to unsubscribe from provider", target, eventName);
    };
  });
}

// libs/sdk/src/lib/wallet/provider-data-adapter.ts
var ProviderDataAdapter = class {
  constructor(info) {
    this.info = info;
    this.provider$ = new BehaviorSubject(null);
    this.activeAddressInner$ = new BehaviorSubject(null);
    this.providerOrDisconnect$ = merge(
      this.provider$,
      defer(() => this.disconnect$).pipe(map(() => null))
    );
    this.addresses$ = this.providerOrDisconnect$.pipe(
      switchMap((provider) => {
        if (!provider) return of([]);
        return from(this.getAddresses()).pipe(
          switchMap((addresses) => fromEIP1193Event(provider, "accountsChanged").pipe(
            startWith(addresses)
          ))
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.activeAddress$ = combineLatest([
      this.addresses$,
      this.activeAddressInner$
    ]).pipe(
      map(([addresses, activeAddress]) => {
        if (activeAddress) return activeAddress;
        return addresses[0];
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.chainId$ = this.providerOrDisconnect$.pipe(
      switchMap((provider) => {
        if (!provider) return of(null);
        return from(this.getChainId()).pipe(
          switchMap((chainId) => fromEIP1193Event(provider, "chainChanged").pipe(
            map((chainStr) => {
              return parseChainId(chainStr);
            }),
            startWith(chainId)
          ))
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.disconnect$ = this.provider$.pipe(
      filter(Boolean),
      switchMap((provider) => fromEIP1193Event(provider, "disconnect"))
    );
    this.isConnected$ = this.activeAddress$.pipe(
      map((address) => !!address)
    );
    this.info$ = of(info);
  }
  getInfo() {
    return this.info;
  }
  setProvider(provider) {
    this.provider$.next(provider);
  }
  async getAddresses() {
    const provider = this.provider$.value;
    if (!provider) return [];
    return await provider.request({ method: "eth_accounts" });
  }
  async getActiveAddress() {
    const activeAddressInner = this.activeAddressInner$.value;
    if (activeAddressInner) {
      return activeAddressInner;
    }
    const addresses = await this.getAddresses();
    return addresses[0] ?? null;
  }
  async getChainId() {
    const provider = this.provider$.value;
    if (!provider) return null;
    const chainStr = await provider.request({ method: "eth_chainId" }).catch(() => null);
    if (!chainStr) return null;
    return parseChainId(chainStr);
  }
  async isConnected() {
    const address = await this.getActiveAddress();
    return !!address;
  }
  setActiveAddress(address) {
    this.activeAddressInner$.next(address);
  }
};
function parseChainId(chainId) {
  if (typeof chainId === "number") {
    return chainId;
  }
  return parseInt(chainId, 16);
}

// libs/sdk/src/lib/wallet/wallet-errors.ts
function isUserRejectError(error) {
  const { code, message } = error;
  return code === 4001 || message?.toLowerCase().includes("user rejected");
}

// libs/sdk/src/lib/wallet/adapters/universal-browser-extension-adapter.ts
var UniversalBrowserExtensionAdapter = class {
  constructor(providerDetail) {
    this.providerDetail = providerDetail;
    this.client = null;
    this.data = new ProviderDataAdapter(this.providerDetail.info);
  }
  get info() {
    return this.providerDetail.info;
  }
  async connect(chainId) {
    this.client = await createClientAndSyncChain(chainId, this.providerDetail.provider);
    this.data.setProvider(this.providerDetail.provider);
    return true;
  }
  async restoreConnect(chainId, force) {
    this.data.setProvider(this.providerDetail.provider);
    const addresses = await this.data.getAddresses();
    const state = addresses.length > 0 && chainId !== null;
    if (!state) {
      this.data.setProvider(null);
    } else {
      this.client = createClient(chainId, this.providerDetail.provider);
    }
    if (!state && force) {
      return this.connect(chainId);
    }
    return state;
  }
  async disconnect() {
    this.client = null;
    this.data.setProvider(null);
    return true;
  }
  async changeChain(chainId) {
    if (!this.client) return false;
    await this.client.switchChain({ id: chainId });
    return true;
  }
  async isConnected() {
    const addresses = await this.client?.getAddresses() ?? [];
    return addresses.length > 0;
  }
  setActiveAddress(address) {
    this.data.setActiveAddress(address);
  }
  async writeContract(params) {
    if (!await this.isConnected() || !this.client) {
      throw new Error("Wallet not connected");
    }
    const address = await this.data.getActiveAddress();
    return await this.client.writeContract({
      ...params,
      account: address
    });
  }
  async signTypedData(typeData) {
    if (!await this.isConnected() || !this.client) {
      throw new Error("Wallet not connected");
    }
    const address = await this.data.getActiveAddress();
    try {
      return await this.client.signTypedData({
        ...typeData,
        account: address
      });
    } catch (error) {
      if (isUserRejectError(error)) {
        throw error;
      }
      console.log(error);
    }
    const data = JSON.stringify(typeData, stringifyReplacer);
    return await this.providerDetail.provider.request({
      method: "eth_signTypedData_v3",
      params: [address, data]
    });
  }
};
function stringifyReplacer(_, value) {
  if (typeof value === "bigint") {
    return value.toString();
  }
  return value;
}

// libs/sdk/src/lib/wallet/global-data-adapter.ts
import { ChainId } from "@one-inch-community/models";
import {
  BehaviorSubject as BehaviorSubject2,
  defaultIfEmpty,
  defer as defer2,
  distinctUntilChanged,
  firstValueFrom,
  map as map2,
  of as of2,
  shareReplay as shareReplay2,
  startWith as startWith2,
  switchMap as switchMap2,
  takeUntil,
  timer,
  combineLatest as combineLatest2,
  tap,
  subscribeOn,
  asapScheduler,
  filter as filter2
} from "rxjs";
import { isAddressEqual } from "viem";

// libs/sdk/src/lib/wallet/storage.ts
import { storage } from "@one-inch-community/core/storage";
function setChainIdInStorage(chainId) {
  storage.set("chainId", Number(chainId));
}
function getChainIdFromStorage() {
  return storage.get("chainId", Number);
}
function setActiveAddress(address) {
  storage.set("activeAddress", address);
}
function getActiveAddress() {
  return storage.get("activeAddress", String);
}
function setActiveWallet(id) {
  storage.set("activeWallet", id);
}
function getActiveWallet() {
  return storage.get("activeWallet", String);
}
function addConnectedWallet(id) {
  const connectedWalletList = storage.get("connectedWallet", JSON.parse);
  const connectedWalletSet = new Set(connectedWalletList);
  if (connectedWalletSet.has(id)) {
    return;
  }
  connectedWalletSet.add(id);
  storage.set("connectedWallet", [...connectedWalletSet.keys()]);
}
function getConnectedWallet() {
  return storage.get("connectedWallet", JSON.parse);
}
function removeConnectedWallet(id) {
  const connectedWalletList = storage.get("connectedWallet", JSON.parse);
  const connectedWalletSet = new Set(connectedWalletList);
  if (!connectedWalletSet.has(id)) {
    return;
  }
  connectedWalletSet.delete(id);
  storage.set("connectedWallet", [...connectedWalletSet.keys()]);
}

// libs/sdk/src/lib/wallet/adapter-id.ts
import { encodePacked } from "viem";
var idMap = /* @__PURE__ */ new WeakMap();
function adapterId(info) {
  if (idMap.has(info)) {
    return idMap.get(info);
  }
  const id = encodePacked(
    ["string", "string", "string"],
    [
      info.name,
      info.rdns ?? "",
      info.icon
    ]
  ).slice(0, 10);
  idMap.set(info, id);
  return id;
}

// libs/sdk/src/lib/wallet/global-data-adapter.ts
var GlobalDataAdapter = class {
  constructor(dataProvider) {
    this.dataProvider = dataProvider;
    this.chainIdInner$ = new BehaviorSubject2(getChainIdFromStorage() ?? ChainId.eth);
    this.currentActiveAdapter$ = defer2(() => this.dataProvider.update$).pipe(
      startWith2(null),
      map2(() => this.dataProvider.currentActiveAdapter),
      distinctUntilChanged(),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.currentActiveAdapterData$ = this.currentActiveAdapter$.pipe(
      map2((adapter) => adapter?.data ?? null),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.info$ = this.currentActiveAdapterData$.pipe(
      filter2(Boolean),
      switchMap2((data) => data.info$)
    );
    this.addresses$ = this.currentActiveAdapterData$.pipe(
      switchMap2((data) => data?.addresses$ ?? of2([])),
      tap((a) => console.log(a)),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.activeAddress$ = this.currentActiveAdapterData$.pipe(
      switchMap2((data) => data?.activeAddress$ ?? of2(null)),
      tap((address) => address && setActiveAddress(address)),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.chainId$ = combineLatest2([
      this.currentActiveAdapterData$,
      this.chainIdInner$
    ]).pipe(
      switchMap2(([data, chainId]) => data?.chainId$ ?? of2(chainId)),
      tap((chainId) => chainId && setChainIdInStorage(chainId)),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.disconnect$ = this.currentActiveAdapterData$.pipe(
      switchMap2((data) => data?.disconnect$ ?? of2()),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.isConnected$ = this.currentActiveAdapterData$.pipe(
      switchMap2((data) => data?.isConnected$ ?? of2(false)),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    combineLatest2([
      this.activeAddress$,
      this.chainId$
    ]).pipe(
      subscribeOn(asapScheduler)
    ).subscribe();
  }
  getInfo() {
    if (!this.dataProvider.currentActiveAdapter) throw new Error("");
    return this.dataProvider.currentActiveAdapter.data.getInfo();
  }
  async getAddresses() {
    const data = await this.getProviderDataAdapter();
    if (!data) return [];
    return await data.getAddresses();
  }
  async getActiveAddress() {
    if (!this.dataProvider.currentActiveAdapter) {
      return null;
    }
    const data = await this.getProviderDataAdapter();
    if (!data) return null;
    return await data.getActiveAddress();
  }
  async getChainId() {
    if (!this.dataProvider.currentActiveAdapter) {
      return this.chainIdInner$.value;
    }
    const data = await this.getProviderDataAdapter();
    if (!data) return this.chainIdInner$.value;
    const chainId = await data.getChainId();
    if (!chainId) return this.chainIdInner$.value;
    return chainId;
  }
  async isConnected() {
    if (!this.dataProvider.currentActiveAdapter) {
      return false;
    }
    const data = await this.getProviderDataAdapter();
    if (!data) return false;
    return await data.isConnected();
  }
  async isActiveAddress(info, address) {
    const id = adapterId(info);
    const activeAddress = await this.dataProvider.currentActiveAdapter?.data.getActiveAddress() ?? null;
    const activeAdapterInfo = this.dataProvider.currentActiveAdapter?.data.getInfo() ?? null;
    const activeAdapterId = activeAdapterInfo ? adapterId(activeAdapterInfo) : null;
    return !!activeAddress && !!activeAdapterId && id === activeAdapterId && isAddressEqual(activeAddress, address);
  }
  isActiveWallet(info) {
    const id = adapterId(info);
    const activeAdapterInfo = this.dataProvider.currentActiveAdapter?.data.getInfo() ?? null;
    const activeAdapterId = activeAdapterInfo ? adapterId(activeAdapterInfo) : null;
    return !!activeAdapterId && activeAdapterId === id;
  }
  isActiveAddress$(info, address) {
    const id = adapterId(info);
    return combineLatest2([
      this.currentActiveAdapter$,
      this.activeAddress$
    ]).pipe(
      map2(([adapter, activeAddress]) => {
        const activeAdapterId = adapter ? adapterId(adapter.data.getInfo()) : null;
        return !!activeAddress && !!activeAdapterId && id === activeAdapterId && isAddressEqual(activeAddress, address);
      })
    );
  }
  isActiveWallet$(info) {
    const id = adapterId(info);
    return this.currentActiveAdapter$.pipe(
      map2((adapter) => {
        const activeAdapterId = adapter ? adapterId(adapter.data.getInfo()) : null;
        return !!activeAdapterId && activeAdapterId === id;
      })
    );
  }
  setChainId(chainId) {
    this.chainIdInner$.next(chainId);
  }
  getProviderDataAdapter() {
    return firstValueFrom(this.currentActiveAdapterData$.pipe(
      takeUntil(timer(0)),
      defaultIfEmpty(null)
    ));
  }
};

// libs/sdk/src/lib/wallet/injected-provider-detail.ts
function getInjectedProviderSupported() {
  return !!window.ethereum;
}
async function getInjectedProviderDetail(provider = window.ethereum) {
  if (!provider) throw new Error("injected provider not supported");
  return {
    provider,
    info: {
      walletId: "injectedWalletId",
      uuid: "injectedWallet",
      name: getName(),
      icon: await getIcon()
    }
  };
}
var icons = {
  default: () => import("./default-icon-UF676TO4.js").then((m) => m.icon),
  oneInchWallet: () => import("./one-inch-wallet-icon-5SPUNDFK.js").then((m) => m.icon)
};
function getIcon() {
  if (window.ethereum?.isOneInchWallet) {
    return icons.oneInchWallet();
  }
  return icons.default();
}
function getName() {
  if (window.ethereum?.isOneInchWallet) {
    return "1inch wallet";
  }
  return "Browser wallet";
}

// libs/sdk/src/lib/wallet/adapters/wallet-connect-v2-adapter/wallet-connect-v2-adapter.ts
import { firstValueFrom as firstValueFrom2, switchMap as switchMap3, throwError, timer as timer2 } from "rxjs";
var WalletConnectV2Adapter = class {
  constructor(providerDetail) {
    this.providerDetail = providerDetail;
    this.provider = null;
    this.client = null;
    this.data = new ProviderDataAdapter(this.providerDetail.info);
  }
  get info() {
    return this.providerDetail.info;
  }
  async isConnected() {
    return this.provider?.isConnected() ?? false;
  }
  async connect(chainId) {
    if (!this.provider) {
      const provider = await import("./multi-connect-provider-3SU46CE6.js").then((m) => m.MultiConnectProvider.connect());
      this.client = await createClientAndSyncChain(chainId, provider);
      this.data.setProvider(provider);
      this.provider = provider;
    } else {
      await this.provider.connect();
    }
    return true;
  }
  async restoreConnect() {
    if (!this.provider) {
      const provider = await import("./multi-connect-provider-3SU46CE6.js").then((m) => m.MultiConnectProvider.restoreConnect());
      this.client = await createClientAndSyncChain(provider.chainId, provider);
      this.data.setProvider(provider);
      this.provider = provider;
    } else {
      await this.provider.restoreConnect();
    }
    return true;
  }
  async disconnect() {
    this.provider?.disconnect();
    return true;
  }
  async changeChain(chainId) {
    await this.client?.switchChain({ id: chainId });
    return true;
  }
  setActiveAddress(address) {
    this.provider?.setActiveAddress(address);
  }
  async writeContract(params) {
    if (!await this.isConnected() || !this.client) {
      throw new Error("Wallet not connected");
    }
    const address = await this.data.getActiveAddress();
    return await Promise.any([
      this.client.writeContract({
        ...params,
        account: address
      }),
      firstValueFrom2(timer2(60 * 1e3 * 3).pipe(
        // 3 min
        switchMap3(() => throwError(() => new Error("wallet connect request timed out")))
      ))
    ]);
  }
  async signTypedData(typeData) {
    if (!await this.isConnected() || !this.client) {
      throw new Error("Wallet not connected");
    }
    const address = await this.data.getActiveAddress();
    return await Promise.race([
      this.client.signTypedData({
        ...typeData,
        account: address
      }),
      firstValueFrom2(timer2(60 * 1e3 * 3).pipe(
        // 3 min
        switchMap3(() => throwError(() => new Error("wallet connect request timed out")))
      ))
    ]);
  }
};

// libs/sdk/src/lib/wallet/adapters/wallet-connect-v2-adapter/wallet-connect-provider-detail.ts
async function getWalletConnectProviderDetail() {
  return {
    info: {
      walletId: "walletConnectId",
      uuid: "walletConnect",
      name: "Wallet Connect",
      icon: await getIcon2()
    }
  };
}
var icons2 = {
  default: () => import("./wallet-connect-icon-NJ76IV67.js").then((m) => m.icon)
};
function getIcon2() {
  return icons2.default();
}

// libs/sdk/src/lib/wallet/connect-wallet-controller.ts
var WalletControllerImpl = class {
  constructor() {
    this.data = new GlobalDataAdapter(this);
    this.activeAdapters = /* @__PURE__ */ new Map();
    this.update$ = new Subject();
    this.currentActiveAdapterId = null;
    this.adapters = /* @__PURE__ */ new Map();
  }
  get isConnected() {
    return this.currentActiveAdapter !== null;
  }
  get connectedWalletInfo() {
    return this.currentActiveAdapter?.info ?? null;
  }
  get currentActiveAdapter() {
    if (!this.currentActiveAdapterId) return null;
    return this.activeAdapters.get(this.currentActiveAdapterId) ?? null;
  }
  async init() {
    await this.initWallets();
    await this.restoreChainId();
    await this.restoreWalletConnection();
    this.update$.next();
  }
  async getSupportedWallets() {
    const info = [];
    this.adapters.forEach((adapter) => info.push(adapter.data.getInfo()));
    return info.sort((info1, info2) => {
      const id1 = adapterId(info1);
      const id2 = adapterId(info2);
      if (id1 === this.currentActiveAdapterId && id2 !== this.currentActiveAdapterId) {
        return -1;
      }
      if (id1 !== this.currentActiveAdapterId && id2 === this.currentActiveAdapterId) {
        return 1;
      }
      if (this.activeAdapters.has(id1) && this.activeAdapters.has(id2)) {
        return 0;
      }
      if (this.activeAdapters.has(id1) && !this.activeAdapters.has(id2)) {
        return -1;
      }
      if (!this.activeAdapters.has(id1) && this.activeAdapters.has(id2)) {
        return 1;
      }
      return 0;
    });
  }
  async connect(info) {
    const chainId = await this.data.getChainId();
    const id = adapterId(info);
    if (!this.adapters.has(id)) {
      throw new Error(`Invalid wallet info ${info.name} not exist`);
    }
    const connectState = await this.connectSafe(chainId, id);
    this.afterConnectWallet(connectState, id);
    this.update$.next();
    return connectState;
  }
  async addConnection(info) {
    const chainId = await this.data.getChainId();
    const id = adapterId(info);
    if (!this.adapters.has(id)) {
      throw new Error(`Invalid wallet info ${info.name} not exist`);
    }
    const adapter = this.adapters.get(id);
    if (!adapter) {
      throw new Error(`Invalid wallet id`);
    }
    if (!this.activeAdapters.has(id)) {
      throw new Error(`Wallet adapter ${info.name} not connected`);
    }
    let connectState;
    try {
      connectState = await adapter.connect(chainId);
    } catch (error) {
      connectState = false;
    }
    this.update$.next();
    return connectState;
  }
  async disconnect() {
    if (this.currentActiveAdapter === null) return true;
    try {
      const state = await this.currentActiveAdapter.disconnect();
      this.currentActiveAdapterId && this.activeAdapters.delete(this.currentActiveAdapterId);
      this.currentActiveAdapterId && removeConnectedWallet(this.currentActiveAdapterId);
      this.currentActiveAdapterId = null;
      this.update$.next();
      return state;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
  async changeChain(chainId) {
    let state = true;
    if (this.currentActiveAdapter) {
      try {
        state = await this.currentActiveAdapter.changeChain(chainId);
      } catch (error) {
        state = false;
      }
    }
    if (state) {
      this.data.setChainId(chainId);
    }
    return state;
  }
  getDataAdapter(info) {
    const id = adapterId(info);
    const adapter = this.adapters.get(id);
    if (!adapter) {
      throw new Error(`Invalid wallet info ${info.name} not exist`);
    }
    return adapter.data;
  }
  async setActiveAddress(info, address) {
    const id = adapterId(info);
    return await this.setActiveAddressInner(id, address);
  }
  async writeContract(params) {
    if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
      throw new Error("Wallet not connected");
    }
    return await this.currentActiveAdapter.writeContract(params);
  }
  async signTypedData(typeData) {
    if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
      throw new Error("Wallet not connected");
    }
    return await this.currentActiveAdapter.signTypedData(typeData);
  }
  async setActiveAddressInner(id, address) {
    let state = true;
    if (this.currentActiveAdapterId !== id) {
      const chainId = await this.data.getChainId();
      if (!this.adapters.has(id)) {
        throw new Error(`Invalid wallet not exist`);
      }
      state = await this.connectSafe(chainId, id);
      this.afterConnectWallet(state, id);
    }
    if (state) {
      this.currentActiveAdapter?.setActiveAddress(address);
    }
    this.update$.next();
  }
  async connectSafe(chainId, walletId, retry = false) {
    const adapter = this.adapters.get(walletId);
    if (!adapter) {
      throw new Error(`Invalid wallet id`);
    }
    let connectState;
    if (!this.activeAdapters.has(walletId) || retry) {
      try {
        connectState = await adapter.connect(chainId);
      } catch (error) {
        connectState = false;
      }
      connectState && this.activeAdapters.set(walletId, adapter);
    } else {
      connectState = await adapter.isConnected();
      if (!retry && !connectState) {
        connectState = await this.connectSafe(chainId, walletId, true);
      }
    }
    return connectState;
  }
  async restoreConnectSafe(chainId, walletId, force) {
    const adapter = this.adapters.get(walletId);
    if (!adapter) {
      throw new Error(`Invalid wallet id`);
    }
    let connectState;
    if (!this.activeAdapters.has(walletId)) {
      try {
        connectState = await adapter.restoreConnect(chainId, force);
      } catch (error) {
        connectState = false;
      }
      connectState && this.activeAdapters.set(walletId, adapter);
    } else {
      connectState = await adapter.isConnected();
    }
    return connectState;
  }
  async restoreChainId() {
    const chainId = getChainIdFromStorage();
    if (!chainId) return;
    await this.changeChain(chainId);
  }
  async restoreWalletConnection() {
    const activeWalletId = getActiveWallet();
    const connectedWallet = getConnectedWallet();
    if (!connectedWallet) return;
    await this.getSupportedWallets();
    const chainId = await this.data.getChainId();
    for (const id of connectedWallet) {
      if (id === activeWalletId) continue;
      if (!this.adapters.has(id)) continue;
      const connectState2 = await this.restoreConnectSafe(chainId, id).catch(() => false);
      this.afterConnectWallet(connectState2, id);
    }
    if (!activeWalletId || !this.adapters.has(activeWalletId)) return;
    const connectState = await this.restoreConnectSafe(chainId, activeWalletId, true);
    this.afterConnectWallet(connectState, activeWalletId);
    if (!connectState) return;
    const activeAddressFromStore = getActiveAddress();
    if (!activeAddressFromStore || !this.currentActiveAdapter) return;
    const addresses = await this.currentActiveAdapter.data.getAddresses();
    if (!addresses.includes(activeAddressFromStore)) return;
    await this.setActiveAddressInner(activeWalletId, activeAddressFromStore);
  }
  initWallets() {
    return new Promise((resolve) => {
      let skipInjectedProvider = false;
      fromEvent(window, "eip6963:announceProvider").pipe(
        tap2((event) => {
          skipInjectedProvider = isEqualsProviders(window.ethereum, event.detail.provider);
          const id = adapterId(event.detail.info);
          if (!this.adapters.has(id)) {
            this.adapters.set(id, new UniversalBrowserExtensionAdapter(event.detail));
          }
        }),
        debounceTime(100),
        take(1),
        takeUntil2(timer3(100)),
        defaultIfEmpty2(null),
        tap2(async () => {
          if (!skipInjectedProvider && getInjectedProviderSupported()) {
            const injectedProviderDetail = await getInjectedProviderDetail();
            const id = adapterId(injectedProviderDetail.info);
            if (!this.adapters.has(id)) {
              this.adapters.set(id, new UniversalBrowserExtensionAdapter(injectedProviderDetail));
            }
          }
          const wc = await getWalletConnectProviderDetail();
          const wcId = adapterId(wc.info);
          this.adapters.set(wcId, new WalletConnectV2Adapter(wc));
          resolve();
        })
      ).subscribe();
      window.dispatchEvent(new Event("eip6963:requestProvider"));
    });
  }
  afterConnectWallet(connectState, id) {
    if (connectState) {
      this.currentActiveAdapterId = id;
      addConnectedWallet(id);
      setActiveWallet(id);
    } else {
      removeConnectedWallet(id);
      const currentActiveWalletIdFromStore = getActiveWallet();
      if (currentActiveWalletIdFromStore === id) {
        setActiveWallet(null);
      }
      if (this.currentActiveAdapterId === id) {
        this.currentActiveAdapterId = null;
      }
      if (this.activeAdapters.has(id)) {
        const adapter = this.adapters.get(id);
        adapter.disconnect().catch();
        this.activeAdapters.delete(id);
      }
    }
  }
};
function isEqualsProviders(provider1, provider2) {
  if (provider1 === provider2) return true;
  const keys1 = Object.keys(provider1 ?? {});
  const keys2 = Object.keys(provider2 ?? {});
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (provider1[key] !== provider2[key]) {
      return false;
    }
  }
  return true;
}
function createConnectWalletController() {
  return new WalletControllerImpl();
}

// libs/sdk/src/lib/wallet/connect-wallet-embedded-controller.ts
import { Subject as Subject2 } from "rxjs";
var ConnectWalletEmbeddedController = class {
  constructor(config) {
    this.config = config;
    this.currentActiveAdapter = null;
    this.activeAdapters = /* @__PURE__ */ new Map();
    this.data = new GlobalDataAdapter(this);
    this.update$ = new Subject2();
  }
  get isConnected() {
    return true;
  }
  get connectedWalletInfo() {
    return this.currentActiveAdapter?.info ?? null;
  }
  async init() {
    if (!this.config.walletProvider) return;
    const injectedProviderDetail = await getInjectedProviderDetail(this.config.walletProvider);
    const id = adapterId(injectedProviderDetail.info);
    const adapter = new UniversalBrowserExtensionAdapter(injectedProviderDetail);
    this.activeAdapters.set(id, adapter);
    await adapter.connect(this.config.chainId);
    this.currentActiveAdapter = adapter;
    this.update$.next();
  }
  async getSupportedWallets() {
    const injectedProviderDetail = await getInjectedProviderDetail(this.config.walletProvider);
    return [injectedProviderDetail.info];
  }
  async writeContract(params) {
    if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
      throw new Error("Wallet not connected");
    }
    return await this.currentActiveAdapter.writeContract(params);
  }
  async signTypedData(typeData) {
    if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
      throw new Error("Wallet not connected");
    }
    return await this.currentActiveAdapter.signTypedData(typeData);
  }
  async changeChain(chainId) {
    if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
      throw new Error("Wallet not connected");
    }
    return await this.currentActiveAdapter.changeChain(chainId);
  }
  connect(info) {
    throw new Error("Method not implemented.");
  }
  addConnection(info) {
    throw new Error("Method not implemented.");
  }
  disconnect() {
    throw new Error("Method not implemented.");
  }
  getDataAdapter(info) {
    throw new Error("Method not implemented.");
  }
  setActiveAddress(info, address) {
    throw new Error("Method not implemented.");
  }
};
export {
  ConnectWalletEmbeddedController,
  createConnectWalletController,
  getChainIdFromStorage
};
//# sourceMappingURL=index.esm.js.map
